import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useState } from "react";
import { SUBABAS_EQUIP_ENSAIO, TABS } from "./config";
import { useFaixaDeAbas } from "./faixa-abas";
import type { Refs, Row } from "./types";
import { api, setSessionUser } from "./api";
import {
  ICONS,
  IconDashboard,
  IconCalendar,
  IconSun,
  IconMoon,
  IconLogout,
  IconHome,
  IconChevronDown,
} from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import { AvisoReuniao } from "./components/PedirReuniaoView";
import Login from "./components/Login";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import Dashboard from "./components/Dashboard";
import AgendaView from "./components/AgendaView";
import CrudView from "./components/CrudView";
import AlmoxarifadoView from "./components/AlmoxarifadoView";
import AvaliarFolgaView from "./components/AvaliarFolgaView";
import PainelRelatoriosView from "./components/PainelRelatoriosView";
import AprovarAgendamentoView from "./components/AprovarAgendamentoView";
import RelatorioInspecaoView from "./components/RelatorioInspecaoView";
import ControleTrafosView from "./components/ControleTrafosView";
import EquipamentosEnsaioView from "./components/EquipamentosEnsaioView";
import AccessGate from "./components/AccessGate";
import AvisoSistema from "./components/AvisoSistema";
import ManutencaoAviso from "./components/ManutencaoAviso";
import { PermissoesProvider } from "./permissoes";
import { ordenarPorNome } from "./ordem";
import { rotuloRef } from "./format";
import { ACESSO_COMPLETO, ativarModoLeitura, podeVerAba, type Visualizacao } from "./modo-leitura";
import {
  readSsoUser,
  clearSsoFromUrl,
  goToSystem,
  SYSTEMS,
  checkAcesso,
  isAdminSupremo,
} from "./integration";

/** Estado do portão de acesso (Fase 2 — bloqueio por sistema). */
type GateStatus = "checking" | "allowed" | "denied" | "error";

const SESSION_KEY = "sercamp-operacional-user";

/** Login inicial: prioriza o SSO vindo do Portal/outros sistemas (URL) e cai na sessão salva. */
function getInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Aba inicial via deep-link do Portal (#tab=chave); cai em "dashboard" se inválida. */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && TABS.some((t) => t.key === k) ? k : "dashboard";
  } catch {
    return "dashboard";
  }
})();

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(getInitialUser);

  useEffect(() => {
    clearSsoFromUrl();
  }, []);
  const [active, setActive] = useState<string>(INITIAL_TAB);
  /*
   * SUBMENU DE "EQUIPAMENTOS DE ENSAIO" — as quatro etapas (Cadastro, Estoque,
   * Saída, Retorno) descem dentro do próprio item do menu. O item de cima abre
   * SEMPRE a lista de sempre (null = lista); a etapa escolhida é daqui, e a
   * tela só avisa de volta quando a pessoa escolhe por lá ("← Voltar").
   * Recolher é só pela seta: clicar no item é o caminho de volta à lista, e não
   * pode sumir com o submenu de quem só queria a lista.
   */
  const [equipSub, setEquipSub] = useState<string | null>(null);
  const [equipAberto, setEquipAberto] = useState<boolean>(INITIAL_TAB === "equipamentos");
  const [refs, setRefs] = useState<Refs>({
    colaboradores: [],
    obras: [],
    equipamentos: [],
    veiculos: [],
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Só administradores excluem registros (regra do Portal) — as lixeiras da
  // árvore inteira saem da tela quando isto é falso.
  const [podeExcluir, setPodeExcluir] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  // Colaborador vinculado ao login (para o aviso de manutenção direcionado).
  const [colaboradorId, setColaboradorId] = useState<number | null>(null);
  // Permissões de aba do usuário: null = sem restrição (admin/legado -> vê tudo).
  const [permissoes, setPermissoes] = useState<string[] | null>(null);
  // Enquanto o /api/me não resolve, não renderiza o app (evita "flash" de abas).
  const [permsLoaded, setPermsLoaded] = useState(false);
  // Portão de acesso: enquanto não confirmado, não renderiza o app.
  const [gate, setGate] = useState<GateStatus>(user ? "checking" : "allowed");
  // Somente visualização (cadastro do Portal): abas liberadas lá e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);
  /* No celular o menu vira uma faixa que rola de lado: isto mantem a aba
     aberta a vista (ver src/faixa-abas.tsx). */
  const navRef = useFaixaDeAbas(active, gate === "allowed");

  // Fase 2 — portão de acesso: sempre que houver usuário atual (SSO novo ou sessão
  // restaurada), confirma com o Portal se ele pode usar ESTE sistema antes de liberar.
  useEffect(() => {
    if (!user) {
      setGate("allowed");
      return;
    }
    // Admin Supremo: sempre liberado (não depende da rede para o portão).
    if (isAdminSupremo(user)) {
      setGate("allowed");
      return;
    }
    let cancelled = false;
    setGate("checking");
    checkAcesso(user)
      .then((r) => {
        if (cancelled) return;
        setVis({ somenteLeitura: r.somenteLeitura, abas: r.abas });
        setGate(r.allowed ? "allowed" : "denied");
      })
      .catch(() => {
        if (!cancelled) setGate("error");
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Resolve se o usuário logado é administrador (define quem vê/gerencia "Usuários").
  useEffect(() => {
    setSessionUser(user ?? "");
    if (!user) {
      setIsAdmin(false);
      setPodeExcluir(false);
      setDisplayName(null);
      setColaboradorId(null);
      setPermissoes(null);
      setPermsLoaded(false);
      return;
    }
    setPermsLoaded(false);
    api
      .me(user)
      .then((r) => {
        setIsAdmin(!!r.isAdmin);
        setPodeExcluir(!!r.podeExcluir);
        setDisplayName(r.nome || null);
        setColaboradorId(r.colaborador_id ?? null);
        setPermissoes(r.isAdmin ? null : r.permissoes ?? null);
        setPermsLoaded(true);
      })
      .catch(() => {
        // Fail-closed: se não deu para confirmar as permissões, não libera nada
        // (não reaproveita null, que significa "sem restrição").
        setIsAdmin(false);
        setPodeExcluir(false);
        setDisplayName(null);
        setColaboradorId(null);
        setPermissoes([]);
        setPermsLoaded(true);
      });
  }, [user]);

  // Pode ver esta aba? Admin e usuário sem restrição (permissoes=null) veem tudo;
  // "usuarios" é sempre exclusivo do admin.
  // Somente visualização (Portal) entra como um E: além das permissões daqui,
  // a aba tem de estar liberada lá — e "Usuários"/"Solicitar Atualização" somem.
  const canView = useCallback(
    (key: string): boolean => {
      if (!podeVerAba(vis, key)) return false;
      if (vis.somenteLeitura && (key === "usuarios" || key === "solicitar_atualizacao")) return false;
      if (key === "usuarios") return isAdmin;
      /* "Solicitar Atualização" é de TODO MUNDO, inclusive do colaborador com
         acesso restrito: é justamente quem usa a tela no dia a dia que percebe
         o que precisa mudar. Ela não depende de permissão. */
      if (key === "solicitar_atualizacao") return true;
      if (isAdmin || permissoes === null) return true;
      // Aba irmã (Manutenção de Veículos) usa a permissão da aba dona.
      const chave = TABS.find((t) => t.key === key)?.permissaoKey ?? key;
      return permissoes.includes(chave);
    },
    [isAdmin, permissoes, vis],
  );

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(gate === "allowed" && !!user && vis.somenteLeitura);
  }, [gate, user, vis.somenteLeitura]);

  // Se a aba ativa não é permitida (ex.: colaborador entra em "dashboard"),
  // redireciona para a primeira aba liberada.
  useEffect(() => {
    if (canView(active)) return;
    const ordem = ["dashboard", "agenda", ...TABS.map((t) => t.key)];
    const primeira = ordem.find((k) => canView(k));
    setActive(primeira ?? "agenda");
  }, [active, canView]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  const loadRefs = useCallback(async () => {
    try {
      const [colaboradores, obras, equipamentos, veiculos] = await Promise.all([
        api.list("colaboradores") as Promise<Row[]>,
        api.list("obras") as Promise<Row[]>,
        api.list("equipamentos") as Promise<Row[]>,
        // A aba de manutenção escolhe o veículo por aqui.
        api.list("veiculos") as Promise<Row[]>,
      ]);
      // As listas de referência já saem daqui em ordem alfabética (src/ordem.ts):
      // é delas que vivem os selects, a Liberação e as linhas da Agenda. O
      // veículo não tem nome — é identificado pela placa (ver rotuloRef).
      setRefs({
        colaboradores: ordenarPorNome(colaboradores, (c) => c.nome),
        obras: ordenarPorNome(obras, (o) => o.nome),
        equipamentos: ordenarPorNome(equipamentos, (e) => e.nome),
        veiculos: ordenarPorNome(veiculos, rotuloRef),
      });
    } catch {
      /* mostrado nas telas individuais */
    }
  }, []);

  useEffect(() => {
    if (user) loadRefs();
  }, [user, loadRefs]);

  const onDataChanged = useCallback(() => {
    loadRefs();
    setRefreshKey((k) => k + 1);
  }, [loadRefs]);

  function login(name: string) {
    localStorage.setItem(SESSION_KEY, name);
    setUser(name);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setUser(null);
    setVis(ACESSO_COMPLETO);
  }

  if (!user) {
    return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;
  }

  // Portão de acesso (Fase 2): só libera o app quando o Portal confirma o acesso.
  if (gate !== "allowed") {
    // Acesso negado: anula a sessão salva (um reload não restaura o app).
    if (gate === "denied") localStorage.removeItem(SESSION_KEY);
    return (
      <AccessGate
        status={gate === "checking" ? "checking" : gate === "denied" ? "denied" : "error"}
        user={user}
        theme={theme}
      />
    );
  }

  // Aguarda as permissões (/api/me) antes de montar o menu — sem flash de abas.
  if (!permsLoaded) {
    return <AccessGate status="checking" user={user} theme={theme} />;
  }

  const tab = TABS.find((t) => t.key === active);
  // Uma etapa de Equipamentos de Ensaio aberta: o título diz qual.
  const equipEtapa =
    active === "equipamentos" ? SUBABAS_EQUIP_ENSAIO.find((s) => s.key === equipSub) : undefined;
  const title =
    active === "dashboard"
      ? "Dashboard"
      : active === "agenda"
        ? "Agenda"
        : equipEtapa
          ? `${tab?.label ?? "Equipamentos de Ensaio"} · ${equipEtapa.titulo}`
          : tab?.label ?? "";
  const subtitle =
    active === "dashboard"
      ? "Visão geral dos indicadores operacionais"
      : active === "agenda"
        ? "Cronograma de serviços internos e externos"
        : equipEtapa
          ? equipEtapa.descricao
          : tab?.subtitle ?? "";

  // O Provider embrulha a árvore inteira: qualquer tela que mostre lixeira lê
  // a permissão pelo hook, sem precisar receber a prop de mão em mão.
  return (
    <PermissoesProvider value={podeExcluir}>
      <AvisoSistema />
      <AvisoReuniao usuario={user} onAbrir={() => setActive("solicitar_atualizacao")} />
      <ManutencaoAviso colaboradorId={colaboradorId} />
      <div className="app">
        <AnimatedBackground />

        <aside className="sidebar">
          <div className="brand">
            <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
          </div>

          <nav className="nav" ref={navRef}>
            {canView("dashboard") && (
              <button
                className={`nav-item ${active === "dashboard" ? "active" : ""}`}
                onClick={() => setActive("dashboard")}
              >
                <span className="nav-icon">
                  <IconDashboard size={19} />
                </span>
                Dashboard
              </button>
            )}
            {canView("agenda") && (
              <button
                className={`nav-item ${active === "agenda" ? "active" : ""}`}
                onClick={() => setActive("agenda")}
              >
                <span className="nav-icon">
                  <IconCalendar size={19} />
                </span>
                Agenda
              </button>
            )}
            {TABS.filter((t) => canView(t.key)).map((t) => {
              const Icon = ICONS[t.icon];
              // "Equipamentos de Ensaio" abre um mini menu com as quatro etapas
              // (SUBABAS_EQUIP_ENSAIO). O item volta sempre à lista; a seta
              // recolhe/abre sem trocar de tela.
              if (t.key === "equipamentos") {
                return (
                  <div key={t.key} className="nav-grupo">
                    <button
                      className={`nav-item nav-item-pai ${active === t.key ? "active" : ""}`}
                      onClick={() => {
                        setActive(t.key);
                        setEquipSub(null);
                        setEquipAberto(true);
                      }}
                      aria-expanded={equipAberto}
                    >
                      <span className="nav-icon">
                        <Icon size={19} />
                      </span>
                      {t.label}
                      <span
                        className={`nav-chevron ${equipAberto ? "aberto" : ""}`}
                        title={equipAberto ? "Recolher as etapas" : "Mostrar as etapas"}
                        aria-hidden="true"
                        onClick={(e) => {
                          // Só a seta: o clique não chega ao item (que abriria a lista).
                          e.stopPropagation();
                          setEquipAberto((v) => !v);
                        }}
                      >
                        <IconChevronDown size={16} />
                      </span>
                    </button>
                    {equipAberto && (
                      <div className="nav-sub">
                        {SUBABAS_EQUIP_ENSAIO.map((sub) => {
                          const ativo = active === t.key && equipSub === sub.key;
                          return (
                            <button
                              key={sub.key}
                              className={`nav-subitem ${ativo ? "active" : ""}`}
                              title={sub.descricao}
                              onClick={() => {
                                setActive(t.key);
                                setEquipSub(sub.key);
                              }}
                            >
                              <span className={`nav-subitem-sigla tom-${sub.tom}`}>{sub.sigla}</span>
                              <span className="nav-subitem-nome">{sub.titulo}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={t.key}
                  className={`nav-item ${active === t.key ? "active" : ""}`}
                  onClick={() => setActive(t.key)}
                >
                  <span className="nav-icon">
                    <Icon size={19} />
                  </span>
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="content">
          <header className="topbar">
            <div>
              <h1>{title}</h1>
              <p className="muted">{subtitle}</p>
            </div>

            <div className="topbar-actions">
              <button
                className="theme-toggle"
                onClick={() => goToSystem(SYSTEMS.portal, user)}
                title="Portal SERCAMP"
              >
                <IconHome size={18} />
              </button>
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              >
                {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <div className="user-chip">
                <span className="user-avatar">
                  {user.charAt(0).toUpperCase()}
                </span>
                <span className="user-name">{displayName || user}</span>
              </div>
              <button className="theme-toggle" onClick={logout} title="Sair">
                <IconLogout size={18} />
              </button>
            </div>
          </header>

          <section className="view">
            {!canView(active) ? (
              <div className="empty">Selecione uma opção no menu.</div>
            ) : active === "dashboard" ? (
              <Dashboard refreshKey={refreshKey} />
            ) : active === "agenda" ? (
              <AgendaView refs={refs} />
            ) : active === "almoxarifado" ? (
              <AlmoxarifadoView user={user} />
            ) : active === "avaliar_folga" ? (
              <AvaliarFolgaView />
            ) : active === "aprovar_agendamento" ? (
              <AprovarAgendamentoView />
            ) : active === "painel_relatorios" ? (
              <PainelRelatoriosView />
            ) : active === "controle_trafos" ? (
              <ControleTrafosView somenteLeitura={vis.somenteLeitura} />
            ) : active === "relatorio_inspecao" ? (
              <RelatorioInspecaoView />
            ) : active === "equipamentos" && equipSub ? (
              // Uma das quatro etapas (cadastro → estoque → saída → retorno). É o
              // mesmo arquivo do Laboratório; sem etapa, a aba é a lista de sempre
              // (o CrudView logo abaixo). "← Voltar" devolve à lista.
              <EquipamentosEnsaioView
                key="equipamentos_ensaio"
                subKey={equipSub}
                onSubKeyChange={(k) => {
                  setEquipSub(k);
                  if (k) setEquipAberto(true);
                }}
                onDataChanged={onDataChanged}
              />
            ) : tab?.key === "solicitar_atualizacao" ? (
              // A aba que existe em todos os sistemas: o pedido vai para o
              // sistema de Desenvolvimento (ver server/solicitar-atualizacao.ts).
              <SolicitarAtualizacaoView usuario={user} />
            ) : tab ? (
              <CrudView key={tab.key} tab={tab} refs={refs} onDataChanged={onDataChanged} />
            ) : null}
          </section>
        </main>
      </div>
    </PermissoesProvider>
  );
}
